import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { clientNotesActions } from "@fitness-app/app-store/src/store/reducers/traineeNotes";
import { type ClientNote, type ClientNoteWithAuthorData } from "@fitness-app/data-models/entities/ClientNote";

import { traineeKeys } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase, useAppDispatch } from "~/store/initializeStore";

// Hooki biorą id klientki z argumentu, a nie z TraineeScope - modal notatki z czatu
// (ChatUtilitiesProvider) działa też poza profilem, np. na Dashboardzie.
export const traineeNotesKey = (traineeId: string) => [...traineeKeys.all(traineeId), "notes"] as const;

export const useTraineeNotes = (traineeId: string) =>
  useQuery({
    queryKey: traineeNotesKey(traineeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_note")
        .select("*, author:authorId(avatarUrl, firstName, lastName)")
        .order("createdAt", { ascending: false })
        .eq("traineeId", traineeId)
        .returns<ClientNoteWithAuthorData[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });

const useInvalidateTraineeNotes = () => {
  const queryClient = useQueryClient();

  return (traineeId: string) => {
    void queryClient.invalidateQueries({ queryKey: traineeNotesKey(traineeId) });
    void queryClient.invalidateQueries(["nutritionNotes", traineeId]);
    void queryClient.invalidateQueries(["floatingNotes", traineeId]);
    void queryClient.invalidateQueries(["traineeDashboardNote", traineeId]);
  };
};

export const useAddTraineeNote = () => {
  const dispatch = useAppDispatch();
  const invalidate = useInvalidateTraineeNotes();

  return useMutation({
    mutationFn: (note: ClientNote) => dispatch(clientNotesActions.addClientNote({ note })).unwrap(),
    onSettled: (_data, _error, note) => invalidate(note.traineeId),
  });
};

interface EditTraineeNoteInput {
  traineeId: string;
  noteId: string;
  authorId: string | null;
  note: Partial<ClientNote>;
}

export const useEditTraineeNote = () => {
  const dispatch = useAppDispatch();
  const invalidate = useInvalidateTraineeNotes();

  return useMutation({
    mutationFn: (input: EditTraineeNoteInput) => dispatch(clientNotesActions.editClientNote(input)).unwrap(),
    onSettled: (_data, _error, { traineeId }) => invalidate(traineeId),
  });
};

export const useRemoveTraineeNote = () => {
  const dispatch = useAppDispatch();
  const invalidate = useInvalidateTraineeNotes();

  return useMutation({
    mutationFn: (input: { traineeId: string; noteId: string }) =>
      dispatch(clientNotesActions.removeClientNote(input)).unwrap(),
    onSettled: (_data, _error, { traineeId }) => invalidate(traineeId),
  });
};
